import { type CSSProperties } from "react";
import { Plus } from "lucide-react";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

/**
 * The sticky page header for the Integration transactions page.
 *
 * Rendered via AppLayout's pageHeader slot. Mobile responsive:
 * tighter padding and smaller type on narrow widths.
 *
 * The "Request integration" button is a filled dark primary button
 * (not ghost) that opens a mailto to Novala support. When the
 * dedicated request form is built in a follow-up PR, this handler
 * navigates to that page instead.
 */

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
};

const REQUEST_MAILTO =
  "mailto:support@novala.app?subject=Integration%20request";

export function IntegrationTransactionsHeader() {
  const { isMobile } = useBreakpoint();

  const wrapStyle: CSSProperties = {
    padding: isMobile ? "16px 16px 12px" : "24px 32px 16px",
  };

  const titleStyle: CSSProperties = {
    fontSize: isMobile ? 20 : 24,
    fontWeight: 800,
    margin: 0,
    color: PALETTE.ink,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
  };

  const subtitleStyle: CSSProperties = {
    fontSize: isMobile ? 12 : 13,
    color: PALETTE.ink3,
    marginTop: 4,
    fontWeight: 500,
    maxWidth: 640,
    lineHeight: 1.55,
  };

  return (
    <div style={wrapStyle}>
      <div style={breadcrumbStyle}>
        <span>Accounting</span>
        <span style={sepStyle}>/</span>
        <span style={currentStyle}>Integration transactions</span>
      </div>
      <div style={rowStyle}>
        <div style={titleBlockStyle}>
          <h1 style={titleStyle}>Integration transactions</h1>
          <div style={subtitleStyle}>
            Skip the manual entry. Connect your sales channels and payment
            processors to automate transaction imports.
          </div>
        </div>
        <div style={actionsStyle}>
          <a href={REQUEST_MAILTO} style={primaryLinkStyle}>
            <Plus size={14} strokeWidth={2.5} />
            Request integration
          </a>
        </div>
      </div>
    </div>
  );
}

const breadcrumbStyle: CSSProperties = {
  fontSize: 12.5,
  color: PALETTE.ink3,
  fontWeight: 500,
  marginBottom: 4,
};

const sepStyle: CSSProperties = {
  color: PALETTE.ink4,
  margin: "0 5px",
};

const currentStyle: CSSProperties = {
  color: PALETTE.ink,
  fontWeight: 600,
};

const rowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: 12,
  flexWrap: "wrap",
};

const titleBlockStyle: CSSProperties = {
  minWidth: 0,
};

const actionsStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  alignItems: "center",
  flexShrink: 0,
  flexWrap: "wrap",
};

const primaryLinkStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: PALETTE.ink,
  color: "#ffffff",
  border: 0,
  fontSize: 13,
  fontWeight: 600,
  padding: "10px 16px",
  borderRadius: 10,
  cursor: "pointer",
  fontFamily: "inherit",
  textDecoration: "none",
  boxShadow: "0 1px 2px rgba(14, 26, 26, 0.08)",
};